import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { PlanFile, PlanStep, ValidationIssue } from '@verifyloop/core';
import { describeError } from '../api/client';
import {
  createEnvironment,
  createTest,
  deleteEnvironment,
  deleteProject,
  flakyCheck,
  getProject,
  getRun,
  listEnvironments,
  listRuns,
  listTests,
  replacePlan,
  rerunTest,
  setDefaultEnvironment,
} from '../api/endpoints';
import type { Run, TestSummary } from '../api/types';
import { useResource, useToast } from '../state';
import { formatDuration, formatRelative } from '../lib/format';
import { PageHeader } from '../components/AppShell';
import { CopyButton } from '../components/CopyButton';
import { EmptyState, ErrorNotice, LoadingRows, SectionTitle } from '../components/Feedback';
import { ConfirmButton, Modal, SelectField, TextArea, TextField } from '../components/Form';
import { GenerateTestModal } from '../components/GenerateTestModal';
import { PlanEditor } from '../components/PlanEditor';
import {
  Duration,
  FailureKindPill,
  Mono,
  RunStatusPill,
  RunTriggerTag,
} from '../components/Pills';
import { RunDetail } from '../components/RunDetail';
import { PlaybookInspectModal } from '../components/PlaybookInspectModal';
import { UnifiedTestWorkspace } from '../components/UnifiedTestWorkspace';
import { FeatureGroupedTests } from '../components/FeatureGroupedTests';
import { PersonasTab } from '../components/PersonasTab';
import {
  PROJECT_TYPE_OPTIONS,
  downloadPlanFile,
  localPlanIssues,
  serializePlan,
  starterPlan,
  toTestCreateBody,
} from '../lib/plan';

const TABS = [
  { key: 'tests', label: 'Tests & Playbook', icon: '📋' },
  { key: 'personas', label: 'Personas & Vault', icon: '🛡️' },
  { key: 'environments', label: 'Environments', icon: '⚙️' },
  { key: 'runs', label: 'Runs History', icon: '📊' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

export function ProjectDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab: TabKey = TABS.some((entry) => entry.key === tabParam) ? (tabParam as TabKey) : 'tests';
  const selectedRun = params.get('run');

  const project = useResource(() => getProject(id), [id], { live: ['project.updated'] });
  const meta = project.data?.project;

  const setTab = (next: TabKey): void => {
    const draft = new URLSearchParams(params);
    draft.set('tab', next);
    draft.delete('run');
    draft.delete('test');
    setParams(draft, { replace: true });
  };

  return (
    <div className="page project-detail-page">
      <PageHeader
        title={meta?.name ?? 'Project'}
        subtitle={
          meta ? (
            <span className="page-header__facts" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  background: 'rgba(59, 130, 246, 0.12)',
                  color: '#60a5fa',
                  padding: '2px 8px',
                  borderRadius: 6,
                  border: '1px solid rgba(59, 130, 246, 0.25)',
                  fontSize: 12,
                }}
              >
                {meta.originMode === 'local' ? '🚇' : '🌐'}
                <span className="mono">{meta.url ?? `localhost:${meta.localPort ?? '?'}`}</span>
              </span>

              <span
                style={{
                  fontSize: 11,
                  padding: '2px 6px',
                  borderRadius: 4,
                  background: 'rgba(255, 255, 255, 0.06)',
                  color: 'var(--text-muted)',
                }}
              >
                {meta.type === 'frontend' ? 'Browser App' : 'API Service'}
              </span>

              <span className="mono text-muted" style={{ fontSize: 11 }}>
                ID: {meta.id}
              </span>
              <span className="text-muted" style={{ fontSize: 12 }}>
                Updated {formatRelative(meta.updatedAt)}
              </span>
            </span>
          ) : (
            'Loading project…'
          )
        }
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Link className="btn btn--ghost btn--small" to="/">
              ← All projects
            </Link>
            {meta ? (
              <ConfirmButton
                label="Delete project"
                confirmLabel="Really delete project?"
                onConfirm={() => {
                  void deleteProject(meta.id)
                    .then(() => {
                      toast.success(`Deleted project “${meta.name}”`);
                      navigate('/');
                    })
                    .catch((error: unknown) => toast.error(describeError(error)));
                }}
              />
            ) : null}
          </div>
        }
      />

      {project.error ? (
        <ErrorNotice error={project.error} onRetry={project.reload} title="Could not load this project" />
      ) : null}

      <div className="tabs" role="tablist" aria-label="Project sections">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            role="tab"
            id={`tab-${entry.key}`}
            aria-selected={tab === entry.key}
            aria-controls={`panel-${entry.key}`}
            className={`tab${tab === entry.key ? ' is-active' : ''}`}
            onClick={() => setTab(entry.key)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <span>{entry.icon}</span>
            <span>{entry.label}</span>
          </button>
        ))}
      </div>

      <div id={`panel-${tab}`} role="tabpanel" aria-labelledby={`tab-${tab}`} className="tabpanel">
        {tab === 'tests' ? (
          <TestsTab
            projectId={id}
            selectedTestId={params.get('test')}
            onSelectTest={(testId) => {
              const draft = new URLSearchParams(params);
              draft.set('tab', 'tests');
              if (testId) draft.set('test', testId);
              else draft.delete('test');
              setParams(draft, { replace: false });
            }}
            onProjectChanged={project.reload}
          />
        ) : null}
        {tab === 'personas' && meta ? (
          <PersonasTab project={meta} onProjectUpdated={project.reload} />
        ) : null}
        {tab === 'environments' ? <EnvironmentsTab projectId={id} onProjectChanged={project.reload} /> : null}
        {tab === 'runs' ? (
          <RunsTab
            projectId={id}
            selectedRunId={selectedRun}
            onSelect={(runId) => {
              const draft = new URLSearchParams(params);
              draft.set('tab', 'runs');
              if (runId) draft.set('run', runId);
              else draft.delete('run');
              setParams(draft, { replace: false });
            }}
          />
        ) : null}
      </div>

      {selectedRun && tab === 'runs' ? (
        <div
          className="drawer-backdrop"
          onMouseDown={(event) => event.target === event.currentTarget && closeRun(setParams, params)}
        >
          <RunDetail runId={selectedRun} onClose={() => closeRun(setParams, params)} />
        </div>
      ) : null}
    </div>
  );
}

type SetSearchParams = (
  next: URLSearchParams,
  options?: { replace?: boolean },
) => void;

function closeRun(setParams: SetSearchParams, params: URLSearchParams): void {
  const draft = new URLSearchParams(params);
  draft.delete('run');
  setParams(draft, { replace: true });
}

// ------------------------------------------------------------------- tests

/**
 * Ready-made plans for a project that has no tests.
 *
 * Each is a real, runnable plan, and each is deliberately **neutral** about
 * the app: no invented credentials, no invented URLs, no claim about what the
 * user's product says. The first starter asks for the app's own name so the
 * plan has a real chance of passing and a real chance of teaching, since every
 * "green on the demo app" template is a promise that breaks the moment a
 * customer clicks it on their own product.
 *
 * A starter that fails on arrival is worse than no starter: it teaches the
 * user that a red result means the tool is broken.
 */
interface StarterTest {
  name: string;
  description: string;
  planSteps: PlanStep[];
}

const STARTER_TESTS: StarterTest[] = [
  {
    name: 'The app loads',
    description: 'Open your app and confirm it renders. The safest first test.',
    planSteps: [
      { type: 'navigation', description: 'Navigate to /' },
      { type: 'action', description: 'Take a screenshot of the home page' },
    ],
  },
  {
    name: 'Sign-in form is present',
    description: 'Go to /login and confirm the sign-in form renders.',
    planSteps: [
      { type: 'navigation', description: 'Navigate to /login' },
      { type: 'assertion', description: 'Verify the page shows a login or sign-in form' },
    ],
  },
  {
    name: 'Key content on the home page',
    description: 'A template to edit: replace “your product name” with your text. It fails until you do.',
    planSteps: [
      { type: 'navigation', description: 'Navigate to /' },
      { type: 'assertion', description: 'Verify the page shows your product name' },
    ],
  },
];

function TestsTab({
  projectId,
  selectedTestId,
  onSelectTest,
  onProjectChanged,
}: {
  projectId: string;
  selectedTestId?: string | null;
  onSelectTest?: (testId: string | null) => void;
  onProjectChanged: () => void;
}): JSX.Element {
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<TestSummary | null>(null);
  const [inspecting, setInspecting] = useState<TestSummary | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPlan, setAiPlan] = useState<Partial<PlanFile> | null>(null);

  const tests = useResource(() => listTests(projectId), [projectId], {
    live: ['test.created', 'test.updated', 'run.created', 'run.finished'],
  });

  /**
   * Create a test from one of the ready-made plans.
   *
   * Asking a new user to author a plan from nothing is the step they give up
   * on: the empty state used to explain the philosophy and leave the writing to
   * them. One click that produces a real, runnable test is the difference
   * between understanding the product and abandoning it.
   */
  const createStarterTest = async (starter: StarterTest): Promise<void> => {
    if (seeding) return;
    setSeeding(true);
    try {
      await createTest({
        projectId,
        type: 'frontend',
        name: starter.name,
        planSteps: starter.planSteps,
      });
      tests.reload();
      toast.success(
        `Created “${starter.name}”`,
        'Run it for a verdict. Edit any step that does not match your app.',
      );
    } catch (error) {
      toast.error(`Could not create that test: ${describeError(error)}`);
    } finally {
      setSeeding(false);
    }
  };

  const runTest = async (test: TestSummary): Promise<void> => {
    setPendingId(test.id);
    try {
      const accepted = await rerunTest(test.id, { wait: true, timeoutMs: 300_000 });
      if (accepted.runIds.length === 0) {
        toast.push({
          kind: 'error',
          message: `“${test.name}” did not start`,
          detail: accepted.skipped[0]?.reason ?? 'the run queue refused it',
        });
        return;
      }
      const run = await getRun(accepted.runIds[0] ?? '');
      if (run.run.status === 'passed') {
        toast.success(`“${test.name}” passed`, `run ${run.run.id} · ${formatDuration(run.run.durationMs)}`);
      } else {
        toast.push({
          kind: 'error',
          message: `“${test.name}” ${run.run.status}${run.run.failureKind ? ` (${run.run.failureKind})` : ''}`,
          detail: run.run.error ?? 'open the Runs tab to read the failure bundle.',
        });
      }
      tests.reload();
      onProjectChanged();
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setPendingId(null);
    }
  };

  const checkFlaky = async (test: TestSummary): Promise<void> => {
    setPendingId(test.id);
    toast.info(`Flaky check running for “${test.name}”`, '3 repeats, healing off: this takes a moment.');
    try {
      const result = await flakyCheck(test.id, 3);
      const verdict =
        result.verdict === 'stable'
          ? 'stable: every repeat passed'
          : result.verdict === 'flaky'
            ? 'flaky: the outcome is not trustworthy'
            : 'broken: it does not pass';
      toast.push({
        kind: result.verdict === 'stable' ? 'success' : 'error',
        message: `“${test.name}” is ${verdict}`,
        detail: `stability ${result.stabilityScore} across ${result.runs.length} run(s)`,
      });
      tests.reload();
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setPendingId(null);
    }
  };

  const selectedTest = selectedTestId ? tests.data?.tests.find((t) => t.id === selectedTestId) : null;

  if (selectedTest) {
    return (
      <>
        <UnifiedTestWorkspace
          test={selectedTest}
          projectId={projectId}
          onBack={() => onSelectTest?.(null)}
          onEditPlan={() => setEditing(selectedTest)}
          onTestChanged={() => {
            tests.reload();
            onProjectChanged();
          }}
        />

        {editing ? (
          <TestPlanDialog
            projectId={projectId}
            test={editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              tests.reload();
              onProjectChanged();
            }}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      <SectionTitle
        actions={
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              type="button"
              className="btn btn--small btn--secondary"
              onClick={() => setAiOpen(true)}
              title="Draft a test plan from PRD requirements or user stories using AI (BYOK or Hosted)"
            >
              ✨ Generate with AI
            </button>
            <button type="button" className="btn btn--small" onClick={() => setCreating(true)}>
              New test
            </button>
          </div>
        }
      >
        Tests
      </SectionTitle>

      {tests.error ? <ErrorNotice error={tests.error} onRetry={tests.reload} /> : null}
      {tests.loading ? <LoadingRows rows={4} label="Loading tests" /> : null}

      {tests.data && tests.data.tests.length === 0 ? (
        <EmptyState title="No tests in this project yet">
          <p>
            Start from a ready-made test, generate a plan from your PRD with AI, or write a plan in
            plain language: an <code>action</code>, an <code>assertion</code>, and Justloop turns
            it into the Playwright run a human would have written.
          </p>
          <div style={{ marginBottom: '20px' }}>
            <button
              type="button"
              className="btn btn--small"
              onClick={() => setAiOpen(true)}
            >
              ✨ Generate test from PRD with AI (BYOK / Hosted)
            </button>
          </div>
          <div className="starter-tests">
            {STARTER_TESTS.map((starter) => (
              <button
                key={starter.name}
                type="button"
                className="starter-tests__item"
                onClick={() => void createStarterTest(starter)}
              >
                <strong>{starter.name}</strong>
                <span>{starter.description}</span>
              </button>
            ))}
          </div>
        </EmptyState>
      ) : null}

      {tests.data && tests.data.tests.length > 0 ? (
        <FeatureGroupedTests
          tests={tests.data.tests}
          pendingId={pendingId}
          onSelectTest={(testId) => (onSelectTest ? onSelectTest(testId) : setInspecting(tests.data?.tests.find(t => t.id === testId) ?? null))}
          onRunTest={(test) => void runTest(test)}
          onCheckFlaky={(test) => void checkFlaky(test)}
          onEditPlan={(test) => setEditing(test)}
          onTestDeleted={() => tests.reload()}
        />
      ) : null}

      {aiOpen ? (
        <GenerateTestModal
          projectId={projectId}
          onClose={() => setAiOpen(false)}
          onPlanGenerated={(generated) => {
            setAiPlan({
              name: generated.name,
              description: generated.description,
              type: generated.type as any,
              planSteps: generated.planSteps as any,
            });
            setCreating(true);
          }}
        />
      ) : null}

      {creating ? (
        <TestPlanDialog
          projectId={projectId}
          initialPlan={aiPlan ?? undefined}
          onClose={() => {
            setCreating(false);
            setAiPlan(null);
          }}
          onSaved={() => {
            tests.reload();
            onProjectChanged();
          }}
        />
      ) : null}

      {editing ? (
        <TestPlanDialog
          projectId={projectId}
          test={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            tests.reload();
            onProjectChanged();
          }}
        />
      ) : null}

      {inspecting ? (
        <PlaybookInspectModal
          test={inspecting}
          onClose={() => setInspecting(null)}
          onEditPlan={() => setEditing(inspecting)}
          onTestUpdated={() => {
            tests.reload();
            onProjectChanged();
          }}
        />
      ) : null}
    </>
  );
}

function TestPlanDialog({
  projectId,
  test,
  initialPlan,
  onClose,
  onSaved,
}: {
  projectId: string;
  test?: TestSummary;
  initialPlan?: Partial<PlanFile>;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const toast = useToast();
  const [plan, setPlan] = useState<PlanFile>(() => {
    if (initialPlan) {
      return {
        ...starterPlan((initialPlan.type as any) || 'frontend', projectId),
        ...initialPlan,
      } as PlanFile;
    }
    return starterPlan('frontend', projectId);
  });
  const [serverIssues, setServerIssues] = useState<readonly ValidationIssue[]>([]);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [jsonOpen, setJsonOpen] = useState(false);

  // Editing starts from the stored plan, not from a template.
  const [hydrated, setHydrated] = useState(false);
  if (test && !hydrated) {
    setHydrated(true);
    setPlan({
      type: test.type,
      name: test.name,
      ...(test.description ? { description: test.description } : {}),
      ...(test.tags.length > 0 ? { tags: test.tags } : {}),
      ...(test.stepTimeoutMs !== undefined ? { stepTimeoutMs: test.stepTimeoutMs } : {}),
      planSteps: test.planSteps,
    });
  }

  const localIssues = useMemo(() => localPlanIssues(plan), [plan]);
  const allIssues = useMemo(() => [...serverIssues, ...localIssues], [serverIssues, localIssues]);
  const fatal = allIssues.filter((issue) => issue.severity === 'error');

  const submit = async (): Promise<void> => {
    setPending(true);
    setError(null);
    setServerIssues([]);
    try {
      if (test) {
        await replacePlan(test.id, plan.planSteps);
        toast.success(`Plan saved for “${plan.name}”`);
      } else {
        const created = await createTest(toTestCreateBody(plan, projectId));
        const warnings = created.warnings.filter((issue) => issue.severity === 'warning');
        toast.success(
          `Test “${created.test.name}” created`,
          warnings.length > 0 ? `${warnings.length} warning(s) from the server` : undefined,
        );
      }
      onSaved();
      onClose();
    } catch (cause) {
      const message = describeError(cause);
      setError(cause instanceof Error ? cause : new Error(message));
      if (cause instanceof Error && 'issues' in cause) {
        const issues = (cause as { issues?: readonly ValidationIssue[] }).issues;
        if (issues && issues.length > 0) setServerIssues(issues);
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <Modal
      wide
      title={test ? `Edit plan: ${test.name}` : 'New test'}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setJsonOpen((open) => !open)}
            aria-expanded={jsonOpen}
          >
            {jsonOpen ? 'Hide plan JSON' : 'Show plan JSON'}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => downloadPlanFile(plan, `${plan.name || 'verifyloop.plan'}.json`)}
            title="Save this plan so `verifyloop test create --plan-from file.json` can replay it"
          >
            Export plan file
          </button>
          <CopyButton value={serializePlan(plan)} label="Copy plan JSON" />
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn" onClick={() => void submit()} disabled={pending || fatal.length > 0}>
            {pending ? 'Validating…' : test ? 'Save plan' : 'Create test'}
          </button>
        </>
      }
    >
      {!test ? (
        <>
          <TextField
            label="Name"
            value={plan.name}
            onChange={(name) => setPlan({ ...plan, name })}
            required
            placeholder="Login rejects an empty password"
          />
          <SelectField
            label="Type"
            value={plan.type}
            onChange={(type) => setPlan({ ...plan, type })}
            options={PROJECT_TYPE_OPTIONS}
          />
          <TextArea
            label="Description"
            rows={2}
            value={plan.description ?? ''}
            onChange={(description) => setPlan({ ...plan, description })}
            hint="Optional. What this test proves, in one sentence."
          />
          <div className="row-fields">
            <TextField
              label="Tags"
              value={(plan.tags ?? []).join(', ')}
              onChange={(value) =>
                setPlan({
                  ...plan,
                  tags: value
                    .split(',')
                    .map((tag) => tag.trim())
                    .filter(Boolean),
                })
              }
              hint="Comma separated."
            />
            <TextField
              label="Step timeout (ms)"
              value={plan.stepTimeoutMs === undefined ? '' : String(plan.stepTimeoutMs)}
              onChange={(value) =>
                setPlan({
                  ...plan,
                  ...(value.trim() === '' ? {} : { stepTimeoutMs: Number(value) }),
                })
              }
              type="number"
            />
          </div>
        </>
      ) : null}

      <PlanEditor plan={plan} onChange={setPlan} serverIssues={serverIssues} disabled={pending} />

      {jsonOpen ? (
        <textarea
          className="input input--area input--code"
          rows={12}
          readOnly
          value={serializePlan(plan)}
          aria-label="Plan JSON"
        />
      ) : null}

      {error ? <ErrorNotice error={error} title="The server rejected this plan" /> : null}
      {fatal.length === 0 && allIssues.length > 0 ? (
        <p className="muted">Warnings only: this plan will be accepted.</p>
      ) : null}
    </Modal>
  );
}

// ----------------------------------------------------------- environments

function EnvironmentsTab({
  projectId,
  onProjectChanged,
}: {
  projectId: string;
  onProjectChanged: () => void;
}): JSX.Element {
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const environments = useResource(() => listEnvironments(projectId), [projectId]);

  const submit = async (): Promise<void> => {
    setPending(true);
    setError(null);
    try {
      await createEnvironment(projectId, { name, ...(url ? { url } : {}), isDefault });
      toast.success(`Environment “${name}” added`);
      setAdding(false);
      setName('');
      setUrl('');
      setIsDefault(false);
      environments.reload();
      onProjectChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <SectionTitle
        actions={
          <button type="button" className="btn btn--small" onClick={() => setAdding((open) => !open)} aria-expanded={adding}>
            {adding ? 'Cancel' : 'Add environment'}
          </button>
        }
      >
        Environments
      </SectionTitle>

      {adding ? (
        <div className="panel panel--form">
          <div className="row-fields">
            <TextField label="Name" value={name} onChange={setName} placeholder="staging" />
            <TextField label="URL" value={url} onChange={setUrl} placeholder="https://staging.example.com" type="url" />
          </div>
          <label className="checkbox">
            <input type="checkbox" checked={isDefault} onChange={(event) => setIsDefault(event.target.checked)} />
            Make this the default environment
          </label>
          {error ? <ErrorNotice error={error} /> : null}
          <button type="button" className="btn btn--small" onClick={() => void submit()} disabled={pending || !name}>
            {pending ? 'Adding…' : 'Add environment'}
          </button>
        </div>
      ) : null}

      {environments.error ? <ErrorNotice error={environments.error} onRetry={environments.reload} /> : null}
      {environments.loading ? <LoadingRows rows={2} label="Loading environments" /> : null}

      {environments.data ? (
        <ul className="rows">
          {environments.data.environments.map((environment) => (
            <li key={environment.id} className="row">
              <div className="row__main">
                <div className="row__title">
                  <strong>{environment.name}</strong>
                  {environment.isDefault ? <span className="pill pill--pass pill--subtle">default</span> : null}
                </div>
                <p className="row__meta">
                  <Mono>{environment.url ?? 'no url (reached through a tunnel)'}</Mono>
                  <span>created {formatRelative(environment.createdAt)}</span>
                  {environment.credentials?.kind && environment.credentials.kind !== 'none' ? (
                    <span>credentials: {environment.credentials.kind}</span>
                  ) : null}
                </p>
              </div>
              <div className="row__actions">
                {!environment.isDefault ? (
                  <button
                    type="button"
                    className="btn btn--small btn--ghost"
                    onClick={() => {
                      void setDefaultEnvironment(projectId, environment.id)
                        .then(() => {
                          toast.success(`“${environment.name}” is now the default`);
                          environments.reload();
                          onProjectChanged();
                        })
                        .catch((error: unknown) => toast.error(describeError(error)));
                    }}
                  >
                    Make default
                  </button>
                ) : null}
                <ConfirmButton
                  label="Delete"
                  confirmLabel="Really delete?"
                  disabled={environment.isDefault}
                  title={environment.isDefault ? 'The default environment cannot be deleted' : undefined}
                  onConfirm={() => {
                    void deleteEnvironment(environment.id)
                      .then(() => {
                        toast.success(`Deleted “${environment.name}”`);
                        environments.reload();
                      })
                      .catch((error: unknown) => toast.error(describeError(error)));
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

// -------------------------------------------------------------------- runs

function RunsTab({
  projectId,
  selectedRunId,
  onSelect,
}: {
  projectId: string;
  selectedRunId: string | null;
  onSelect: (runId: string | null) => void;
}): JSX.Element {
  const runs = useResource(() => listRuns({ project: projectId, limit: 100 }), [projectId], {
    live: ['run.created', 'run.updated', 'run.finished'],
  });

  return (
    <>
      <SectionTitle>Runs</SectionTitle>
      {runs.error ? <ErrorNotice error={runs.error} onRetry={runs.reload} /> : null}
      {runs.loading ? <LoadingRows rows={5} label="Loading runs" /> : null}

      {runs.data && runs.data.runs.length === 0 ? (
        <EmptyState title="No runs yet">
          <p>Run a test from the Tests tab, or use <code>verifyloop test run --wait</code> from the CLI.</p>
        </EmptyState>
      ) : null}

      {runs.data && runs.data.runs.length > 0 ? (
        <div className="table-wrap">
          <table className="table table--stacked-mobile">
            <caption className="visually-hidden">Runs for this project, newest first</caption>
            <thead>
              <tr>
                <th scope="col">status</th>
                <th scope="col">failure</th>
                <th scope="col">started</th>
                <th scope="col">duration</th>
                <th scope="col">trigger</th>
                <th scope="col">steps</th>
                <th scope="col">run id</th>
              </tr>
            </thead>
            <tbody>
              {runs.data.runs.map((run: Run) => (
                <tr
                  key={run.id}
                  className={`table__row${selectedRunId === run.id ? ' is-selected' : ''}`}
                  onClick={() => onSelect(run.id)}
                >
                  <td data-label="status">
                    <button
                      type="button"
                      className="linkish"
                      onClick={(event) => {
                        event.stopPropagation();
                        onSelect(run.id);
                      }}
                      aria-label={`Open run ${run.id}`}
                    >
                      <RunStatusPill status={run.status} />
                    </button>
                  </td>
                  <td data-label="failure">
                    <FailureKindPill kind={run.failureKind} />
                  </td>
                  <td data-label="started">{formatRelative(run.startedAt)}</td>
                  <td data-label="duration">
                    <Duration ms={run.durationMs} />
                  </td>
                  <td data-label="trigger">
                    <RunTriggerTag trigger={run.trigger} />
                  </td>
                  <td data-label="steps" className="num">
                    {run.steps.filter((step) => step.status === 'passed').length}/{run.steps.length}
                  </td>
                  <td data-label="run id">
                    <Mono title={run.id}>{run.id.slice(0, 10)}…</Mono>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
